/**
 * La transposition des styles par caractère d'un texte lié (plan bound-text-styles §3.2) —
 * le miroir de CompositionDocument::TextStyles (Ruby), vérifié par les mêmes cas
 * (test/fixtures/files/style_transposition_cases.json).
 *
 * Les styles sont indexés par position ; l'interpolation déplace tout. La carte des
 * segments (lib variable_interpolation) dit où chaque plage de l'expression atterrit : une
 * partie fixe garde ses styles décalés ; un token ou un bloc prend, pour toute sa valeur,
 * le style de son premier caractère (« tout ou rien »). La carte inversée ramène les styles
 * du résultat vers l'expression — ce que fait l'export quand le canvas affiche des valeurs.
 *
 * Forme des styles : celle que Fabric sérialise (`stylesToArray`) — des plages
 * `{ start, end, style }` en index plats de graphèmes, retours à la ligne exclus.
 */
import type { Segment } from "./variable_interpolation";

export interface StyleRange {
  start: number;
  end: number;
  style: Record<string, unknown>;
}

/** Les styles de l'espace source (`from`) portés dans l'espace cible (`to`) de la carte. */
export function transposeStyles(styles: StyleRange[], segments: Segment[]): StyleRange[] {
  const source = new Map<number, Record<string, unknown>>();
  for (const range of styles) {
    for (let i = range.start; i < range.end; i++) source.set(i, range.style);
  }

  const out: StyleRange[] = [];
  for (const segment of segments) {
    for (let i = segment.to[0]; i < segment.to[1]; i++) {
      const at = segment.kind === "text" ? segment.from[0] + (i - segment.to[0]) : segment.from[0];
      const style = source.get(at);
      if (!style || Object.keys(style).length === 0) continue;

      const last = out[out.length - 1];
      if (last && last.end === i && sameStyle(last.style, style)) last.end = i + 1;
      else out.push({ start: i, end: i + 1, style: { ...style } });
    }
  }
  return out;
}

/** La carte dans l'autre sens : du résultat vers l'expression. */
export function invertSegments(segments: Segment[]): Segment[] {
  return segments.map((s) => ({ kind: s.kind, from: s.to, to: s.from }));
}

function sameStyle(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => a[k] === b[k]);
}

/**
 * Une position (index plat) portée de l'espace `to` vers l'espace `from` de la carte : dans
 * une partie fixe, décalée ; dans une valeur, à la borne de son token — le début pour un
 * début de sélection, la fin pour une fin.
 */
export function mapIndexBack(segments: Segment[], index: number, side: "start" | "end"): number {
  for (const segment of segments) {
    const [a, b] = segment.to;
    if (index < a || index > b) continue;
    if (segment.kind === "text") return segment.from[0] + (index - a);
    if (index === a && side === "start") return segment.from[0];
    if (index === b && side === "end") return segment.from[1];
    if (index > a && index < b) return side === "start" ? segment.from[0] : segment.from[1];
  }
  const last = segments[segments.length - 1];
  return last ? last.from[1] : index;
}
